import { BalanceType } from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { createStoreWithSubscribe, createShallowStore } from '@frontend/store';

export type PaymentSelectAllFilters = {
  balanceType: BalanceType[];
  searchValue: string;
};

export type PaymentSelectAllStore = {
  isAllSelected: boolean;
  filters: PaymentSelectAllFilters;
  included: string[];
  excluded: string[];
  selectedCount: number;
  setIsAllSelected: (isAllSelected: boolean, count: number) => void;
  setFilters: (filters: Partial<PaymentSelectAllFilters>) => void;
  handleSelection: (isSelected: boolean, id: string) => void;
  handleBulkExclusion: (ids: string[]) => void;
  reset: () => void;
};

const defaultFilters: PaymentSelectAllFilters = {
  balanceType: [],
  searchValue: '',
};

export const useBulkCollectionSelectAllStore = createStoreWithSubscribe<PaymentSelectAllStore>(
  (set) => ({
    isAllSelected: false,
    filters: defaultFilters,
    included: [],
    excluded: [],
    selectedCount: 0,
    setIsAllSelected: (isAllSelected: boolean, count: number) =>
      set((state) => ({ ...state, isAllSelected, selectedCount: count })),
    setFilters: (filters: Partial<PaymentSelectAllFilters>) =>
      set((state) => ({ ...state, filters: { ...state.filters, ...filters } })),
    handleSelection: (isSelected: boolean, id: string) => {
      set((state) => {
        if (isSelected) {
          if (state.included.includes(id)) return state;

          return {
            ...state,
            included: [...state.included, id],
            excluded: state.excluded.filter((e) => e !== id),
            selectedCount: state.selectedCount + 1,
          };
        }
        if (state.isAllSelected) {
          if (state.excluded.includes(id)) return state;

          return {
            ...state,
            excluded: [...state.excluded, id],
            included: state.included.filter((e) => e !== id),
            selectedCount: state.selectedCount - 1,
          };
        }
        if (!state.included.includes(id)) return state;

        return { ...state, included: state.included.filter((e) => e !== id), selectedCount: state.selectedCount - 1 };
      });
    },
    handleBulkExclusion: (ids: string[]) => {
      set((state) => {
        const newExcluded = ids.filter((id) => !state.excluded.includes(id));
        const selectedExclusions = state.isAllSelected
          ? newExcluded
          : newExcluded.filter((id) => state.included.includes(id));
        const excluded = [...new Set([...state.excluded, ...newExcluded])];
        const included = state.included.filter((e) => !ids.includes(e));
        return { ...state, included, excluded, selectedCount: state.selectedCount - selectedExclusions.length };
      });
    },
    reset: () =>
      set((state) => ({
        ...state,
        isAllSelected: false,
        selectedCount: 0,
        filters: defaultFilters,
        included: [],
        excluded: [],
      })),
  }),
  {
    name: 'BulkCollectionSelectAllStore',
    trace: true,
  }
);

export const useBulkCollectionSelectAllShallowStore = createShallowStore(useBulkCollectionSelectAllStore);
